'use client'

import { useMemo } from 'react'
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { GoogleIcon } from '@dd3tech/wiggui/GoogleIcon'
import { Skeleton } from '@dd3tech/wiggui/Skeleton'

import { MAX_HOUR, MIN_HOUR, TRAFFIC_CATEGORIES, TraficLevel } from '../utils'

interface TrafficChartProps {
  trafficLevel: TraficLevel
  trafficHistory: {
    hour: number
    trafficScale: number | null
  }[]
  isLoading?: boolean
}

export const TrafficChart = ({
  trafficLevel,
  trafficHistory,
  isLoading
}: TrafficChartProps) => {
  const traffic = TRAFFIC_CATEGORIES[trafficLevel]

  const history = useMemo(() => {
    const hoursFiltered = [...trafficHistory].slice(MIN_HOUR, MAX_HOUR)
    const history = hoursFiltered.map(({ hour, trafficScale }, index) => {
      return {
        name: hour,
        value:
          index === 0 || index === hoursFiltered.length - 1 ? 0 : trafficScale
      }
    })
    return history
  }, [trafficHistory])

  if (isLoading) {
    return <Skeleton width="w-full" height="h-[168px]" />
  }

  if (!traffic) {
    return null
  }

  return (
    <div className="flex flex-col gap-1 p-4 bg-white border border-strokes rounded-xl">
      <div className="flex justify-between items-center gap-4">
        <div className="text-sm leading-4 font-bold">
          <p className="text-body-m font-normal text-secondary">
            Tráfico en la zona
          </p>
          <p className="text-body-l">{traffic?.title}</p>
        </div>
        <div className="relative overflow-hidden h-[40px] rounded-full">
          <div className="w-full h-full px-3 flex items-center gap-[10px]">
            {Array.from(
              { length: traffic?.iconCount },
              (_, index) => index
            ).map((item) => (
              <GoogleIcon
                key={item}
                name="directions_car"
                className={traffic?.iconClass}
              />
            ))}
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={90}>
        <BarChart
          width={350}
          height={90}
          data={history}
          margin={{
            top: 0,
            right: 0,
            left: -60,
            bottom: -10
          }}
          barGap={12}
        >
          <YAxis
            domain={([, dataMax]) => {
              return [dataMax * 0.7, dataMax]
            }}
            tickCount={100}
            tickLine={false}
            includeHidden
            fontSize={0}
            strokeWidth={0}
          />
          <XAxis
            dataKey="name"
            tickSize={0}
            interval={0}
            fontSize={12}
            stroke="#D1D1D1"
            strokeWidth={0.5}
            padding={{ left: 15, right: 15 }}
            tick={<CostomXAxis />}
          />
          <Bar
            stackId="a"
            dataKey="value"
            fill={traffic?.color}
            radius={8}
            barSize={8}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const CostomXAxis = (props: {
  payload?: {
    index: number
    value: any
  }
  x?: number
  y?: number
}) => {
  const { ...chart } = props

  const hour = chart?.payload?.value
  const isVisible = hour % 3 === 0

  const time = hour % 12 || 12
  const prefix = hour < 12 ? 'am' : 'pm'

  return isVisible ? (
    <g transform={`translate(${chart.x},${chart.y})`}>
      <text
        x={chart?.payload?.index === 0 ? 8 : 15}
        y={12}
        textAnchor="end"
        fill="#121212"
        fontSize={12}
      >
        {time}
        {prefix}
      </text>
    </g>
  ) : (
    <></>
  )
}
